import { createRng } from '../utils/prng';
import { expForStreak } from '../utils/experience';
import { City, Coordinates, GameMode, GameState, RoundResult, StatsStore } from '../types';

/**
 * Picks a city at random while avoiding recent selections to keep matchups fresh.
 */
function pickCity(cities: City[], rng: () => number, excludeIds: Set<string>): City {
  let attempt = 0;
  while (attempt < 50) {
    const city = cities[Math.floor(rng() * cities.length)];
    if (!excludeIds.has(city.id)) {
      return city;
    }
    attempt += 1;
  }
  const fallback = cities.find((c) => !excludeIds.has(c.id));
  if (!fallback) throw new Error('No cities available for selection');
  return fallback;
}

/**
 * Maintains a rolling window of previously seen city ids used for deduping draws.
 */
function updateRollingList(list: string[], next: string, max = 6): string[] {
  const updated = [next, ...list];
  return updated.slice(0, max);
}

export function initGame(
  cities: City[],
  mode: GameMode,
  seed: string,
  bestStreak: number,
  runKind: GameState['runKind'],
  playerLocation?: Coordinates,
): GameState {
  if (cities.length < 2) {
    throw new Error('Need at least two cities to start a game');
  }
  const rng = createRng(seed);
  const first = pickCity(cities, rng, new Set());
  const second = pickCity(cities, rng, new Set([first.id]));
  return {
    mode,
    currentCity: first,
    challengerCity: second,
    streak: 0,
    bestStreak,
    isOver: false,
    daySeed: mode === 'daily' ? seed : undefined,
    stepIndex: 0,
    lastSeenIds: [first.id, second.id],
    cityPool: cities,
    runKind,
    roundHistory: [],
    playerLocation,
  };
}

export interface ChoiceResult {
  correct: boolean;
  winner: City;
  loser: City;
  state: GameState;
  stats: StatsStore;
  historyKey?: string;
  earnedExp: number;
}

/**
 * Updates the stats store after a choice without mutating the original object.
 */
function updateStatsStore(
  stats: StatsStore,
  mode: GameMode,
  delta: { correctGuess?: boolean; completed?: boolean; bestStreak?: number },
): StatsStore {
  const modeStats = stats[mode];
  const updated = {
    ...stats,
    [mode]: {
      totalGames: modeStats.totalGames + (delta.completed ? 1 : 0),
      totalCorrectGuesses: modeStats.totalCorrectGuesses + (delta.correctGuess ? 1 : 0),
      bestStreak: Math.max(modeStats.bestStreak, delta.bestStreak ?? modeStats.bestStreak),
    },
  };
  return updated;
}

export function evaluateChoice(
  state: GameState,
  choice: 'left' | 'right',
  seed: string,
  stats: StatsStore,
): ChoiceResult {
  // Choices are resolved after the challenger has replaced the left-hand city,
  // so the challenger becomes the new baseline for the next round on correct answers.
  if (state.isOver) {
    return {
      correct: false,
      winner: state.currentCity,
      loser: state.challengerCity,
      state,
      stats,
      earnedExp: 0,
    };
  }

  const rng = createRng(`${seed}-${state.stepIndex}`);
  const leftCity = state.currentCity;
  const rightCity = state.challengerCity;
  const previousHistory = state.roundHistory ?? [];
  const populationDiff = Math.abs(leftCity.population - rightCity.population);
  const isTie = populationDiff <= 1000;
  const leftWins = leftCity.population > rightCity.population;
  const playerPickedLeft = choice === 'left';
  const correct = isTie ? true : leftWins === playerPickedLeft;
  const winner = leftWins || isTie ? leftCity : rightCity;
  const loser = leftWins || isTie ? rightCity : leftCity;

  const roundRecord: RoundResult = {
    id: `${state.stepIndex}-${leftCity.id}-${rightCity.id}`,
    leftCity,
    rightCity,
    populationDiff,
    winnerId: winner.id,
    correct,
  };

  if (!correct) {
    const finalState: GameState = {
      ...state,
      // Keep the same matchup visible so the player can see the result.
      currentCity: state.currentCity,
    challengerCity: state.challengerCity,
    isOver: true,
    bestStreak: Math.max(state.bestStreak, state.streak),
    roundHistory: [...previousHistory, roundRecord],
  };
    const updatedStats = updateStatsStore(stats, state.mode, { completed: true, bestStreak: finalState.bestStreak });
    const earnedExp = expForStreak(state.streak, state.mode, state.runKind);
    const historyKey = state.mode === 'daily' && state.daySeed ? state.daySeed.replace('daily-', '') : undefined;
    return {
      correct,
      winner,
      loser,
      state: finalState,
      stats: updatedStats,
      historyKey,
      earnedExp,
    };
  }

  const exclude = new Set<string>([leftCity.id, rightCity.id, ...state.lastSeenIds]);
  const nextCity = pickCity(state.cityPool, rng, exclude);
  const nextState: GameState = {
    ...state,
    currentCity: rightCity,
    challengerCity: nextCity,
    streak: state.streak + 1,
    stepIndex: state.stepIndex + 1,
    bestStreak: Math.max(state.bestStreak, state.streak + 1),
    lastSeenIds: updateRollingList(state.lastSeenIds, nextCity.id),
    roundHistory: [...previousHistory, roundRecord],
  };

  const updatedStats = updateStatsStore(stats, state.mode, {
    correctGuess: true,
    bestStreak: nextState.bestStreak,
  });

  return { correct, winner, loser, state: nextState, stats: updatedStats, earnedExp: 0 };
}
